import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMidpointMediaCustomerOverview } from "../../api/partnerPortalApi";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  badgeClass,
  formatStatusLabel,
} from "./midpointMediaCustomerPortalLabels";
import "../../styles/midpointMediaCustomerPortal.css";

export default function MidpointMediaCustomerDashboardPage() {
  const { partner } = usePartnerAuth();
  const { path } = usePartnerPortalBase();
  const [overview, setOverview] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMidpointMediaCustomerOverview()
      .then((body) => {
        if (!cancelled) setOverview(body);
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load customer portal.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const name =
    partner?.firstName ||
    partner?.displayName ||
    partner?.businessName ||
    "there";
  const summary = overview?.summary;

  return (
    <div className="mm-customer-portal">
      <header className="mm-customer-portal__hero">
        <div>
          <p className="mm-customer-portal__eyebrow">Customer portal</p>
          <h1>Welcome, {name}</h1>
          <p className="mm-customer-portal__intro">
            Report issues, check Midpoint Media infrastructure status, and follow
            the progress of your projects.
          </p>
        </div>
        <div className="mm-customer-portal__actions">
          <Link className="mm-customer-btn mm-customer-btn--primary" to={path("tickets")}>
            Report an issue
          </Link>
          <Link className="mm-customer-btn" to={path("infrastructure")}>
            Infrastructure
          </Link>
          <Link className="mm-customer-btn" to={path("projects")}>
            Projects
          </Link>
        </div>
      </header>

      {loading ? <p className="mm-customer-muted">Loading workspace…</p> : null}
      {errorMessage ? <p className="mm-customer-error">{errorMessage}</p> : null}

      {!loading && !errorMessage && summary ? (
        <>
          <section className="mm-customer-kpi-grid" aria-label="Workspace summary">
            <article className="mm-customer-kpi">
              <span>Open tickets</span>
              <strong>{summary.openTickets}</strong>
            </article>
            <article className="mm-customer-kpi">
              <span>Active projects</span>
              <strong>{summary.projectsActive}</strong>
            </article>
            <article className="mm-customer-kpi">
              <span>Infrastructure</span>
              <strong>
                <span className={badgeClass(summary.infrastructureStatus)}>
                  {formatStatusLabel(summary.infrastructureStatus)}
                </span>
              </strong>
            </article>
          </section>

          <section className="mm-customer-panel">
            <h3>Recent tickets</h3>
            {overview.recentTickets?.length ? (
              <div className="mm-customer-list">
                {overview.recentTickets.map((ticket) => (
                  <article key={ticket.id} className="mm-customer-row">
                    <div className="mm-customer-row__top">
                      <strong>{ticket.subject}</strong>
                      <span className={badgeClass(ticket.status)}>
                        {formatStatusLabel(ticket.status)}
                      </span>
                    </div>
                    <p>
                      {formatStatusLabel(ticket.priority)} priority ·{" "}
                      {formatStatusLabel(ticket.category)}
                    </p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="mm-customer-empty">No open tickets. Report an issue anytime.</p>
            )}
          </section>

          <section className="mm-customer-panel">
            <h3>Projects</h3>
            {overview.projects?.length ? (
              <div className="mm-customer-list">
                {overview.projects.map((project) => (
                  <article key={project.id} className="mm-customer-row">
                    <div className="mm-customer-row__top">
                      <strong>{project.name}</strong>
                      <span className={badgeClass(project.status)}>
                        {formatStatusLabel(project.status)}
                      </span>
                    </div>
                    <p>{project.nextMilestone || project.summary || "—"}</p>
                    <div
                      className="mm-customer-progress"
                      aria-label={`${project.progressPct || 0}% complete`}
                    >
                      <span style={{ width: `${Math.max(0, Math.min(100, project.progressPct || 0))}%` }} />
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="mm-customer-empty">No projects yet.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
